import {keepPreviousData, queryOptions} from '@github-ui/react-query'
import {verifiedFetchJSON} from '@github-ui/verified-fetch'

import type {SavedView, SavedViewsResponse} from './types'

export const SAVED_VIEWS_QUERY_KEY = 'repo-saved-views'
export const SAVED_VIEW_QUERY_KEY = 'repo-saved-view'

export class SavedViewFetchError extends Error {
  readonly status: number

  constructor(status: number, message: string) {
    super(message)
    this.name = 'SavedViewFetchError'
    this.status = status
  }
}

export async function parseFetchErrorMessage(response: Response, fallback: string): Promise<string> {
  return (await parseFetchError(response, fallback)).message
}

export interface ParsedFetchError {
  message: string
  code?: string
}

export async function fetchSavedViews({owner, repo, signal}: {owner: string; repo: string; signal?: AbortSignal}) {
  const response = await verifiedFetchJSON(`/${owner}/${repo}/issues/views.json`, {signal})
  if (!response.ok) {
    throw new SavedViewFetchError(
      response.status,
      await parseFetchErrorMessage(response, `Failed to fetch saved views (${response.status})`),
    )
  }
  return (await response.json()) as SavedViewsResponse
}

export async function parseFetchError(response: Response, fallback: string): Promise<ParsedFetchError> {
  const contentType = response.headers.get('content-type') ?? ''
  if (contentType.includes('application/json')) {
    try {
      const payload = (await response.json()) as {error?: {message?: string; code?: string}} | null
      if (payload?.error?.message) {
        return {message: payload.error.message, code: payload.error.code}
      }
    } catch {
      return {message: fallback}
    }
    return {message: fallback}
  }

  try {
    const text = await response.text()
    if (text) return {message: text}
  } catch {
    return {message: fallback}
  }
  return {message: fallback}
}

export function savedViewsQueryOptions({
  owner,
  repo,
  initialData,
}: {
  owner: string
  repo: string
  initialData?: SavedViewsResponse
}) {
  return queryOptions({
    queryKey: [SAVED_VIEWS_QUERY_KEY, owner, repo] as const,
    placeholderData: keepPreviousData,
    initialData,
    staleTime: initialData ? Infinity : 0,
    queryFn: ({signal}) => fetchSavedViews({owner, repo, signal}),
  })
}

export function savedViewQueryOptions({owner, repo, id}: {owner: string; repo: string; id: string}) {
  return queryOptions({
    queryKey: [SAVED_VIEW_QUERY_KEY, owner, repo, id] as const,
    queryFn: async ({signal}) => {
      const response = await verifiedFetchJSON(`/${owner}/${repo}/issues/views/${id}.json`, {signal})
      if (!response.ok) {
        throw new SavedViewFetchError(
          response.status,
          await parseFetchErrorMessage(response, `Failed to fetch view (${response.status})`),
        )
      }
      return (await response.json()) as {savedView: SavedView; viewerCanManageViews: boolean}
    },
  })
}
